// SPDX-License-Identifier: AGPL-3.0-only
import { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Code,
  CopyButton,
  Group,
  Loader,
  Stack,
  Table,
  Text,
  Title,
} from '@mantine/core';

import { ApiError, api } from '../api/client';
import type { EnrolmentToken, Site } from '../api/client';

/**
 * Gateway enrolment for one Site.
 *
 * WHAT THIS DOES: issues a one-time enrolment token, shows the plaintext ONCE,
 *   lists token metadata and state, and revokes a token that is still eligible.
 * WHAT DEPENDS ON IT: an operator's ability to enrol a Gateway at all. Until
 *   this existed the feature was complete server-side and unusable by a person.
 * WHAT IT IS NOT: an authorization control. Every button here is a courtesy —
 *   the server refuses the request whether or not the button was rendered, and
 *   hiding a control has never protected anything. Controls are hidden or
 *   disabled to avoid offering an action that will fail, not to enforce one.
 *
 * # THE PLAINTEXT LIVES IN MEMORY, AND ONLY IN MEMORY
 *
 * `issued` below is React state. It is never written to localStorage,
 * sessionStorage, IndexedDB, a cookie, the URL, the query string or the
 * fragment, and it is never logged. There is no second request that can
 * retrieve it — the server stores a SHA-256 digest — so losing it means issuing
 * another token, which is the correct trade and is said in words on screen.
 *
 * Refreshing the page therefore loses it. That is the SHOW-ONCE PROPERTY
 * WORKING, not a defect to fix by persisting the secret somewhere convenient.
 *
 * # WHY THERE IS A pagehide LISTENER
 *
 * Navigating away can put a page in the browser's back/forward cache with its
 * JavaScript heap intact, and restoring it would put the plaintext back on
 * screen after the operator believed it was gone — a show-once guarantee that
 * quietly holds a second time. `pagehide` fires in that path (and on ordinary
 * unload), so the secret is cleared deliberately rather than left to whether
 * the browser happened to discard the page.
 */
export function GatewayEnrolment({ site }: { site: Site }) {
  const [tokens, setTokens] = useState<EnrolmentToken[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<ApiError | null>(null);
  const [busy, setBusy] = useState(false);

  // THE ONLY PLACE THE PLAINTEXT EXISTS IN THE BROWSER.
  const [issued, setIssued] = useState<{
    plaintext: string;
    expiresAt: string;
    /**
     * The complete enrolment bundle, assembled here.
     *
     * # WHY THE GUI ASSEMBLES IT
     *
     * The Gateway's `enrol` takes a BUNDLE, not a token: the token plus the
     * installation id, the installation public key it will PIN, and the endpoint
     * it will connect to. An operator asked to write that JSON by hand is an
     * operator transcribing a base64 key at 2am, and the failure mode is a
     * Gateway that cannot enrol for reasons nobody can see.
     *
     * Every field comes from the server's response except the endpoint, which is
     * the origin this page was served from — the same Server the Gateway will
     * talk to, by construction rather than by being typed.
     *
     * IT CONTAINS THE TOKEN, so it is treated exactly like the token: state
     * only, never stored, never logged, and copied only when asked.
     */
    bundle: string;
  } | null>(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.enrolmentTokens(site.id);
      setTokens(result.tokens);
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setLoading(false);
    }
  }, [site.id]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // Changing site must not carry the previous site's secret across.
  useEffect(() => {
    setIssued(null);
  }, [site.id]);

  useEffect(() => {
    const forget = () => setIssued(null);
    window.addEventListener('pagehide', forget);
    return () => window.removeEventListener('pagehide', forget);
  }, []);

  async function issue() {
    setBusy(true);
    setError(null);
    try {
      const result = await api.issueEnrolmentToken(site.id, site.company_id);
      setIssued({
        plaintext: result.enrolment_token,
        expiresAt: result.token.expires_at,
        bundle: JSON.stringify(
          {
            bundle_version: 1,
            enrolment_token: result.enrolment_token,
            installation_id: result.installation_id,
            installation_public_key: result.installation_public_key,
            endpoint: window.location.origin,
          },
          null,
          2,
        ),
      });
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  async function revoke(tokenId: string) {
    setBusy(true);
    setError(null);
    try {
      await api.revokeEnrolmentToken(tokenId);
      await reload();
    } catch (err) {
      setError(err instanceof ApiError ? err : null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card withBorder>
      <Stack>
        <Group justify="space-between" wrap="wrap" gap="sm">
          <div>
            <Title order={4}>Gateway enrolment</Title>
            <Text size="sm" c="dimmed">
              {site.name}
            </Text>
          </div>
          <Button onClick={() => void issue()} loading={busy} disabled={loading}>
            Issue enrolment token
          </Button>
        </Group>

        {issued && (
          <IssuedToken
            plaintext={issued.plaintext}
            bundle={issued.bundle}
            expiresAt={issued.expiresAt}
            onDismiss={() => setIssued(null)}
          />
        )}

        {error && <EnrolmentError error={error} />}

        {loading ? (
          <Group justify="center" p="xl">
            <Loader />
          </Group>
        ) : tokens.length === 0 ? (
          <Text c="dimmed" size="sm">
            No enrolment tokens for this site yet. Issue one to enrol a Gateway.
          </Text>
        ) : (
          <TokenTable tokens={tokens} busy={busy} onRevoke={(id) => void revoke(id)} />
        )}
      </Stack>
    </Card>
  );
}

/**
 * The plaintext, shown once.
 *
 * # WHY THE COPY IS EXPLICIT
 *
 * Writing to the clipboard automatically would put a live credential into a
 * shared surface the operator did not ask to use — one that other applications
 * can read, that syncs between devices on some platforms, and that they cannot
 * see the contents of. The operator asks, or it does not happen.
 */
function IssuedToken({
  plaintext,
  bundle,
  expiresAt,
  onDismiss,
}: {
  plaintext: string;
  bundle: string;
  expiresAt: string;
  onDismiss: () => void;
}) {
  return (
    <Alert color="yellow" title="Copy this enrolment token now">
      <Stack gap="sm">
        <Text size="sm">
          This is the only time this token is shown. It cannot be recovered — if you lose it,
          revoke it and issue another. Closing this or refreshing the page loses it.
        </Text>

        {/* Selectable and wrapping, so it works with a mouse, a keyboard, and a
            thumb on a narrow screen. */}
        <Code
          block
          data-testid="enrolment-token-plaintext"
          style={{ wordBreak: 'break-all', whiteSpace: 'pre-wrap' }}
        >
          {plaintext}
        </Code>

        <Group gap="sm" wrap="wrap">
          <CopyButton value={plaintext}>
            {({ copied, copy }) => (
              <Button onClick={copy} variant={copied ? 'filled' : 'default'} color={copied ? 'green' : undefined}>
                {copied ? 'Copied to clipboard' : 'Copy token'}
              </Button>
            )}
          </CopyButton>
          {/* THE BUNDLE IS WHAT THE GATEWAY ACTUALLY TAKES. The token alone is
              not enough: a Gateway pins the installation key BEFORE it trusts
              the connection it is about to authenticate, so the key has to reach
              it out of band — which is what this bundle is for. */}
          <CopyButton value={bundle}>
            {({ copied, copy }) => (
              <Button onClick={copy} variant={copied ? 'filled' : 'default'} color={copied ? 'green' : undefined}>
                {copied ? 'Bundle copied' : 'Copy gateway bundle'}
              </Button>
            )}
          </CopyButton>
          <Button variant="subtle" onClick={onDismiss}>
            I have saved it
          </Button>
        </Group>

        <Text size="xs" c="dimmed">
          Enrol a Gateway by piping the bundle into it, so the token never reaches a command line:
          {' '}
          <Code>meshvara-gateway enrol --data-dir /var/lib/meshvara-gateway --bundle -</Code>
        </Text>

        <Text size="xs" c="dimmed">
          Expires {formatTimestamp(expiresAt)}
        </Text>
      </Stack>
    </Alert>
  );
}

/**
 * A failure, rendered from the server's problem document.
 *
 * A 403 is called out separately because "you do not have permission" and "the
 * server broke" need different reactions from the person reading it, and the
 * request id is shown because that is the only thing joining what they saw to
 * what the server logged.
 */
function EnrolmentError({ error }: { error: ApiError }) {
  const denied = error.status === 403;
  return (
    <Alert color={denied ? 'orange' : 'red'} title={denied ? 'Not permitted' : error.problem.title}>
      {denied
        ? 'Your account does not have permission to manage Gateway enrolment for this site.'
        : error.problem.detail}
      {error.requestId && (
        <Text size="xs" c="dimmed" mt="xs">
          Request ID: <code>{error.requestId}</code>
        </Text>
      )}
    </Alert>
  );
}

function TokenTable({
  tokens,
  busy,
  onRevoke,
}: {
  tokens: EnrolmentToken[];
  busy: boolean;
  onRevoke: (id: string) => void;
}) {
  return (
    // Scrolls inside itself on a narrow screen rather than pushing the page
    // sideways.
    <Table.ScrollContainer minWidth={480}>
      <Table>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>Status</Table.Th>
            <Table.Th>Issued</Table.Th>
            <Table.Th>Expires</Table.Th>
            <Table.Th>Action</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {tokens.map((token) => {
            const status = tokenStatus(token);
            return (
              <Table.Tr key={token.id}>
                <Table.Td>
                  <Badge color={STATUS_COLOUR[status]} variant="light">
                    {status}
                  </Badge>
                </Table.Td>
                <Table.Td>{formatTimestamp(token.created_at)}</Table.Td>
                <Table.Td>{formatTimestamp(token.expires_at)}</Table.Td>
                <Table.Td>
                  {status === 'unused' ? (
                    <Button
                      size="xs"
                      variant="light"
                      color="red"
                      disabled={busy}
                      onClick={() => onRevoke(token.id)}
                    >
                      Revoke
                    </Button>
                  ) : (
                    // An em dash rather than a disabled button: there is no
                    // action here, and a greyed control invites a click that
                    // cannot mean anything.
                    <Text c="dimmed">—</Text>
                  )}
                </Table.Td>
              </Table.Tr>
            );
          })}
        </Table.Tbody>
      </Table>
    </Table.ScrollContainer>
  );
}

type TokenStatus = 'unused' | 'consumed' | 'revoked' | 'expired';

const STATUS_COLOUR: Record<TokenStatus, string> = {
  unused: 'blue',
  consumed: 'green',
  revoked: 'gray',
  expired: 'orange',
};

/**
 * The status an operator needs to see, which is not exactly the stored state.
 *
 * "Expired" is not a stored value: a token that was never redeemed simply stops
 * being usable when its expiry passes, and the server's redemption predicate is
 * what enforces that. Showing it as "unused" would be technically true and
 * practically misleading — the operator would try to use it.
 *
 * This is display only. Nothing here decides whether a token works.
 */
export function tokenStatus(token: EnrolmentToken, now: Date = new Date()): TokenStatus {
  if (token.state === 'unused' && new Date(token.expires_at).getTime() <= now.getTime()) {
    return 'expired';
  }
  return token.state;
}

/** A timestamp in the reader's own locale and timezone. */
function formatTimestamp(iso: string): string {
  const at = new Date(iso);
  if (Number.isNaN(at.getTime())) return iso;
  return at.toLocaleString();
}
